import * as fetch from "isomorphic-fetch";
import { Promise } from "es6-promise";
import { hashHistory } from "react-router";
import { IRegistrationError, Registration } from "../models/registration";
import Fetcher, { IHttpResult } from "./fetcher";
import { action, observable } from "mobx";

export class RequestInitImpl implements RequestInit {
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit;
  referrer?: string;
  referrerPolicy?: ReferrerPolicy;
  mode?: RequestMode;
  credentials?: RequestCredentials = "same-origin";
  cache?: RequestCache;
  redirect?: RequestRedirect;
  integrity?: string;
  window?: any; // can only be set to null
}

interface IAuthResponse {
  access_token: string;
  email: string;
  expires_in: number;
  firstName: string;
  lastName: string;
  userName: string;
  prevLogin: string;
  acceptTerms: string;
  loginCt: string;
}

interface IErrorMessage {
  error: string[];
}
interface IAuthError {
  message: string;
  modelState: IErrorMessage;
}

export class Auth {
  static tokenKey: string = "token";
  static nameKey: string = "friendly-name";
  static emailKey: string = "email";
  static expiresKey: string = "expires";
  static prevLoginKey: string = "prevLogin";
  static viewKey: string = "lastVw";
  static loginCtKey: string = "loginCt";
  static linkKey: string = "promptLink";

  private static _isAuthenticated: boolean = false;
  private static _reAuthenticated: boolean = false;

  private static clearKeys(clearVw:boolean): void {
    sessionStorage.removeItem(Auth.tokenKey);
    sessionStorage.removeItem(Auth.nameKey);
    sessionStorage.removeItem(Auth.emailKey);
    sessionStorage.removeItem(Auth.expiresKey);
    sessionStorage.removeItem(Auth.loginCtKey);
    if (clearVw) {
      sessionStorage.removeItem(Auth.viewKey);
    }
  }
  static login(user: string, pwd: string, cb: (success: boolean, terms: number) => void): void {
    Auth.reAuthenticated = false;
    let headers: Headers = new Headers();
    headers.append("Content-Type", "application/json");
    let req: RequestInit = new RequestInitImpl();
    req.method = "POST";
    req.body = `grant_type=password&userName=${user}&password=${pwd}`;
    req.headers = headers;
    Auth.clearKeys(false);
    fetch("/token", req).then(function (resp: Response): any {
      if (resp.status >= 400) {
        if (cb) {
          cb(false, 0);
        }
        throw new Error(resp.statusText);
      }
      return resp.json();
    }).then(action((json: IAuthResponse): void => {
      if (json.access_token) {
        sessionStorage.setItem(Auth.tokenKey, json.access_token);
        sessionStorage.setItem(Auth.nameKey, `${json.firstName} ${json.lastName}`);
        sessionStorage.setItem(Auth.emailKey, json.userName);
        sessionStorage.setItem(Auth.prevLoginKey, json.prevLogin);
        sessionStorage.setItem(Auth.expiresKey, (new Date().valueOf() + json.expires_in * 1000).toString());
        sessionStorage.setItem(Auth.loginCtKey, json.loginCt);
        Auth.isAuthenticated = true;
      }
      if (cb) {
        cb(json.access_token !== null && json.access_token.length > 0, parseInt(json.acceptTerms));
      }
    }));
  }
  static register(registration: Registration, cb: (err: IRegistrationError) => void, loginCB?:(success:boolean, terms:number)=>void): void {
    Fetcher.postWithData("api/account/register", registration, (err: IRegistrationError): void => {
      if (!err) {
        Auth.login(registration.email, registration.password, loginCB);
      }
      cb(err);
    }, true);
  }
  
 static logout(cb: () => void): void {
     
    Fetcher.postSimple("api/account/logout", null, action((ok: boolean): void => {
      Auth.isAuthenticated = false;
      Auth.reAuthenticated = false;
      Auth.clearKeys(true);
      cb();
      hashHistory.push("/home");
    }));
  }
  static requestPassword(email: string, cb: (success: boolean) => void): void {
    let req: RequestInit = new RequestInitImpl();
    req.method = "GET";
    fetch(`/api/account/ForgotPassword?email=${email}`, req).then((resp: Response): any => {
      if (resp.status === 200) {
        return resp.json();
      } else {
        cb(false);
      }
    }).then((resp: boolean): void => {
      cb(resp);
    });
  }
  static changePassword(oldPwd: string, newPwd: string, confirmPwd: string, cb: (err: string) => void): void {
    let req: RequestInit = new RequestInitImpl();
    req.method = "POST";
    req.headers = this.headers;
    req.body = JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confirmPwd });
    fetch("/api/account/changepassword", req).then((resp: Response): any => {
      if (resp.status === 200) {
        return "";
      } else {
        return resp.json();
      }
    }).then((resp: IAuthError): void => {
      if (!resp || !resp.modelState.error) {
        cb("");
      } else {
        cb(resp.modelState.error.toString());
      }
    });
  }
  static isEmailUnique = (email: string, cb: (unique:boolean) => void): void => {
      Fetcher.get(`api/account/email/exists?email=${email}`, (exists: boolean): void => {
          cb(!exists);
      });
  }
  static isPasswordValid = (pwd: string, cb:(err: string) => void) => {
    Fetcher.get(`/api/account/password/valid?pwd=${pwd}`, (err: string) => {
      console.log(err);
        cb(err);
    });
  }
  static updatePassword(pwd: string, cb: (success: boolean) => void): void {
    Fetcher.put("api/account/passwordreset", pwd, cb);
  }
  static get isAuthenticated(): boolean {
    return Auth._isAuthenticated;
  }
  static get reAuthenticated(): boolean {
    return Auth._reAuthenticated;
  }
  static set isAuthenticated(auth: boolean) {
    Auth._isAuthenticated = auth;
  }
  static set reAuthenticated(reauth: boolean) {
    Auth._reAuthenticated = reauth;
  }
  static get headers(): Headers {
    let headers: Headers = new Headers();
    headers.append("Content-Type", "application/json");
    const token: string = sessionStorage.getItem(Auth.tokenKey);
    headers.append("Authorization", `Bearer ${token}`);
    return headers;
  }
  static get authHeaders(): Headers {
    let headers: Headers = new Headers();
    const token: string = sessionStorage.getItem(Auth.tokenKey);
    headers.append("Authorization", `Bearer ${token}`);
    return headers;
  }
  static get userName(): string {
    return sessionStorage.getItem(Auth.nameKey);
  }
  static get loginCt(): number {
    const v: string = sessionStorage.getItem(Auth.loginCtKey);
    const num: number = parseInt(v, 10);
    return !isNaN(num) ? num : 0;
  }
  static get lastRoute(): string {
    return sessionStorage.getItem(Auth.viewKey);
  }
  @action static reauthenticate(): void {
    const url: string = sessionStorage.getItem(Auth.viewKey);
    const dtstr: string = sessionStorage.getItem(Auth.expiresKey);
    if (dtstr) {
      const i: number = parseInt(dtstr, 10);
      Auth.isAuthenticated = i > Date.now();
      if (Auth.isAuthenticated && url && window.location.hash !== url) {
        Auth.reAuthenticated = true;
        hashHistory.push(url);
      }
    } else {
      Auth.clearKeys(true);
    }
  }
}


// WEBPACK FOOTER //
// ./src/utils/auth.ts